// src/pages/Wallet/modals/SendNFTModal.js
import React, { useState, useContext, useEffect } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { WalletContext } from '../../../contexts/WalletContext';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import './SendNFTModal.css';

// ERC-721 ABI for NFT transfers
const ERC721_ABI = [
  "function safeTransferFrom(address from, address to, uint256 tokenId) external",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function getApproved(uint256 tokenId) view returns (address)",
  "function isApprovedForAll(address owner, address operator) view returns (bool)",
  "function approve(address to, uint256 tokenId) external",
  "function setApprovalForAll(address operator, bool approved) external"
];

const SendNFTModal = ({ 
  isOpen, 
  onClose, 
  onSend, 
  nft,
  dynamicGasPrice,
  gasOption,
  customGasPrice
}) => {
  const { t } = useTranslation();
  const [receiver, setReceiver] = useState('');
  const [error, setError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const { currentChain } = useContext(NetworkContext);
  const { currentWallet, provider } = useContext(WalletContext);

  // Get NFT details
  const nftData = nft?.nft || {};
  const contractAddress = nftData.contractAddress;
  const tokenId = nftData.tokenId;
  const nftName = nftData.name || `${nftData.collectionName || 'NFT'} #${tokenId}`;

  // Validate receiver address
  useEffect(() => {
    if (!receiver) {
      setError(null);
      return;
    }

    const validateAddress = async () => {
      setIsValidating(true);
      
      try {
        // Check if it's a valid Ethereum address
        if (!ethers.isAddress(receiver)) {
          setError(t('wallet.errors.invalidAddress', 'Invalid address format'));
          return;
        }

        // Check if it's not the same as current wallet
        if (receiver.toLowerCase() === currentWallet?.address?.toLowerCase()) {
          setError(t('wallet.errors.cannotSendToSelf', 'Cannot send to your own address'));
          return;
        }

        // Additional validation: Check if the address is a contract
        // (Optional: some contracts might not be able to receive NFTs)
        const code = await provider.getCode(receiver);
        if (code !== '0x') {
          console.log('Warning: Sending to a contract address');
        }

        setError(null);
      } catch (err) {
        console.error('Error validating address:', err);
        setError(t('wallet.errors.addressValidationFailed', 'Address validation failed'));
      } finally {
        setIsValidating(false);
      }
    };

    // Debounce validation
    const timeoutId = setTimeout(validateAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [receiver, currentWallet?.address, provider, t]);

  // Validate NFT ownership
  const validateNFTOwnership = async () => {
    if (!contractAddress || !tokenId || !currentWallet?.address || !provider) {
      return false;
    }

    try {
      const nftContract = new ethers.Contract(contractAddress, ERC721_ABI, provider);
      const owner = await nftContract.ownerOf(tokenId);
      
      if (owner.toLowerCase() !== currentWallet.address.toLowerCase()) {
        setError(t('wallet.errors.nftNotOwned', 'You do not own this NFT'));
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error validating NFT ownership:', err);
      setError(t('wallet.nftValidationFailed', 'Failed to validate NFT ownership'));
      return false;
    }
  };

  if (!isOpen) return null;

  const handleSendClick = async () => {
    if (!receiver || error || isValidating) return;

    // Validate NFT ownership before proceeding
    const isOwner = await validateNFTOwnership();
    if (!isOwner) return;

    // Call the parent's onSend function
    if (onSend) {
      onSend(nft, receiver);
    }
  };

  const handleReceiverChange = (e) => {
    const value = e.target.value.trim();
    setReceiver(value);
  };

  return (
    <div className="send-nft-modal-overlay">
      <div className="send-nft-modal-content">
        <h2>{t('wallet.sendNFT', 'Send NFT')}</h2>

        {/* NFT Details */}
        <div className="nft-details-section">
          <div className="nft-details-label">{t('wallet.nftToSend', 'NFT to Send')}</div>
          <div className="nft-details-card">
            <div className="nft-image-small">
              {nftData.imageUrl ? (
                <img 
                  src={nftData.imageUrl} 
                  alt={nftName}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="nft-image-fallback-small" style={{ display: nftData.imageUrl ? 'none' : 'flex' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
              </div>
            </div>
            <div className="nft-details-info">
              <div className="nft-name-small">{nftName}</div>
              <div className="nft-collection-small">
                {nftData.collectionName || t('wallet.nft.unknownCollection', 'Unknown Collection')}
              </div>
              <div className="nft-token-id-small">#{tokenId}</div>
            </div>
          </div>
        </div>

        {/* Receiver Address Input */}
        <label className="send-nft-modal-label" htmlFor="nft-receiver">
          {t('wallet.receiver')}
        </label>
        <input
          id="nft-receiver"
          className={`send-nft-modal-input ${error ? 'input-error' : ''}`}
          type="text"
          placeholder={t('wallet.placeholders.enterAddress')}
          value={receiver}
          onChange={handleReceiverChange}
        />

        {/* Validation Status */}
        {isValidating && (
          <div className="validation-status">
            {t('wallet.validatingAddress', 'Validating address...')}
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Warning Message */}
        <div className="nft-warning-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span>
            {t('wallet.nftTransferWarning', 'Make sure the recipient address supports NFTs. Transfers cannot be reversed.')}
          </span>
        </div>

        {/* Button Row */}
        <div className="button-row">
          <div className="button-cell">
            <button 
              className="button-common send-btn" 
              onClick={handleSendClick}
              disabled={!!error || !receiver || isValidating}
            >
              {t('actions.send')}
            </button>
          </div>
          <div className="button-cell">
            <button 
              className="button-common cancel-btn" 
              onClick={onClose}
            >
              {t('actions.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendNFTModal;